import React, { useEffect } from "react";
import { MdOutlineAttachment, MdClose, MdInfoOutline } from "react-icons/md";
import { useSelector } from "react-redux";
import {
  getScenarioHelperContextPdfs,
  addScenarioHelperContextPdf,
  removeScenarioHelperContextPdf,
  resetScenarioHelperContextPdfs,
} from "../state/chatbotSlice";
import { useAppDispatch } from "src/common/state/hooks";
import { closeStandardModal, openStandardModal } from "src/common/state/slice/modal/modalSlice";
import ModalGallery from "src/features/graph/components/ModalGallery";
import { IMedia } from "src/model/unityObject";
import { RootState } from "src/common/state/store";
import { setMediaFilter } from "src/features/gallery/state/gallerySlice";
import Spinner from "src/common/components/Loader/Spinner";
import { useTranslation } from "react-i18next";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { Tooltip } from "@mui/material";

export interface AttachContextPdfsProps {
  onPdfAdded?: (pdf: IMedia) => void;
  isEditionEnabled: boolean;
}

const AttachContextPdfs: React.FC<AttachContextPdfsProps> = ({
  onPdfAdded,
  isEditionEnabled = true,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const selectedPdfs = useSelector(getScenarioHelperContextPdfs);
  const currentFilter = useSelector((state: RootState) => state.media.currentFilter);

  const handleClick = () => {
    if (selectedPdfs.length >= 5) {
      dispatch(
        setAlert({ type: "warning", msg: t("pages.chat.max5Pdf"), customTimeoutInMs: 3000 }),
      );
      return;
    }
    dispatch(setMediaFilter({ ...currentFilter, mediaType: "pdf" }));
    dispatch(openStandardModal("modal-gallery"));
  };

  useEffect(() => {
    return () => {
      dispatch(resetScenarioHelperContextPdfs());
      dispatch(closeStandardModal("modal-gallery"));
    };
  }, [dispatch]);

  return (
    <div className="mt-2 relative min-w-[70%]">
      <button
        onClick={handleClick}
        className="flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
        style={{
          cursor: isEditionEnabled ? "pointer" : "not-allowed",
        }}
        disabled={!isEditionEnabled}
      >
        <span style={{ opacity: isEditionEnabled ? 1 : 0.4 }} className="flex items-center">
          <MdOutlineAttachment size={28} />
          <span className="ml-2 text-sm">{t("pages.chat.attachPdf")}</span>
        </span>
        {isEditionEnabled ? (
          <Tooltip title={t("pages.chat.attachPdfTooltip")}>
            <span className="ml-1 relative text-[12px] text-gray-500 -top-1">
              <MdInfoOutline size={16} />
            </span>
          </Tooltip>
        ) : (
          <Tooltip title={t("pages.chat.attachPdfDisabledTooltip")}>
            <span className="ml-1 relative text-[12px] text-gray-500 -top-1">
              <MdInfoOutline size={16} />
            </span>
          </Tooltip>
        )}
      </button>

      {selectedPdfs && selectedPdfs.length > 0 && (
        <div className="mt-2 space-y-1 relative">
          {selectedPdfs.map((pdf) => (
            <div key={pdf.id} className="flex items-center text-xs text-gray-600 gap-2">
              <span>{pdf.filename}</span>
              {isEditionEnabled ? (
                pdf?.upload_status === "completed" ? (
                  <button
                    onClick={() => dispatch(removeScenarioHelperContextPdf(pdf.id))}
                    className="text-red-500 hover:text-red-700"
                  >
                    <MdClose size={14} />
                  </button>
                ) : (
                  <div className="relative w-[14px] h-[14px]">
                    <div className="absolute inset-0">
                      <div className="w-full h-full flex items-center justify-center">
                        <Spinner
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "14px",
                            height: "14px",
                            margin: 0,
                            marginLeft: "0.3rem",
                            zIndex: 1,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <></>
              )}
            </div>
          ))}
        </div>
      )}

      <ModalGallery
        nodeId="profile"
        onMediaChange={(e: any) => {
          const newPdf = e as IMedia;
          dispatch(addScenarioHelperContextPdf(newPdf));
          if (onPdfAdded) {
            onPdfAdded(newPdf);
          }
          dispatch(closeStandardModal("modal-gallery"));
        }}
        excludedMediaIds={selectedPdfs.map((pdf) => pdf.id)}
      />
    </div>
  );
};

export default AttachContextPdfs;
